import { Edge as ReactFlowEdge } from 'reactflow';

import {
  DagGraph,
  getNodeIdsFromEdgeId,
  getSelectedLineageEdgeIdSet,
  isReactFlowNode,
  ReactFlowNode,
} from '../dag/internalDag';
import { DbtDagNode } from '../dbt-dag';

type HighlightNode<TDbtDagNode extends DbtDagNode> = {
  uniqueIds?: string[];
  nodes: ReactFlowNode<TDbtDagNode>[];
  edges: ReactFlowEdge<any>[];
  dagGraph: DagGraph<any>;
};

export function getHighlightedNodesAndEdges<TDbtDagNode extends DbtDagNode>({
  uniqueIds = [],
  nodes,
  edges,
  dagGraph,
}: HighlightNode<TDbtDagNode>) {
  // Persist selection from prior elements
  const filteredNodes = nodes.filter((el) =>
    isReactFlowNode(el),
  ) as ReactFlowNode<TDbtDagNode>[];
  const hasNode = filteredNodes.some((node) =>
    uniqueIds.some((id) => id === node.data.id),
  );

  const [selectedEdges, selectedNodes] = hasNode
    ? getSelectedLineageEdgeIdSet(dagGraph, uniqueIds)
    : [new Set<string>(), new Set<string>()];
  const newNodes = updateLineageNodeHighlights({
    selectedEdges,
    selectedNodes,
    elements: nodes,
    ids: uniqueIds,
  });
  const newEdges = updateLineageEdgeHighlights({
    selectedEdges,
    selectedNodes,
    elements: edges,
    ids: uniqueIds,
  });

  return {
    nodes: newNodes,
    edges: newEdges,
  };
}

type EdgeHighlightParams<TElement> = {
  selectedEdges: Set<string>;
  selectedNodes: Set<string>;
  elements: TElement[];
  ids: string[];
};

function updateLineageEdgeHighlights({
  selectedEdges,
  selectedNodes,
  elements,
}: EdgeHighlightParams<ReactFlowEdge<any>>): ReactFlowEdge<any>[] {
  const edges = elements.map((edge) => {
    const [, targetNodeId] = getNodeIdsFromEdgeId(edge.id);
    edge.data = {
      ...edge.data,
      highlight: selectedEdges.has(edge.id),
      // Since we cannot define the order in which lines are drawn, all edges
      // which meet at any arrowhead must be given an arrowhead
      hasArrowhead: selectedNodes.has(targetNodeId),
      isHidden: selectedEdges.size > 0 && !selectedEdges.has(edge.id),
    };
    return edge;
  });
  return edges;
}

function updateLineageNodeHighlights({
  selectedNodes,
  elements,
  ids,
}: EdgeHighlightParams<ReactFlowNode<any>>): ReactFlowNode<any>[] {
  const nodes = elements.map((node) => {
    node.data = {
      ...node.data,
      isSelected: ids.some((id) => id === node.id),
      isHidden: selectedNodes.size > 0 && !selectedNodes.has(node.id),
    };
    return node;
  });
  return nodes;
}
